import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useActorFetch } from '../../hooks/useActorFetch';
import { IMAGE_BASE_URL, POSTER_SIZE, BACKDROP_SIZE } from '../../config';
import { movieUrl } from '../../helpers';
import Spinner from '../Spinner';
import NoImage from '../../images/no_image.jpg';

import {
    PageWrapper,
    Hero,
    HeroOverlay,
    ProfileImage,
    HeroInfo,
    ActorName,
    KnownFor,
    MetaRow,
    MetaItem,
    Content,
    SectionTitle,
    Bio,
    FilmGrid,
    FilmCard,
    FilmPoster,
    FilmTitle,
    BackLink,
} from './ActorPage.styles';

const formatDate = iso => {
    if (!iso) return 'Unknown';
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
        });
    } catch {
        return iso;
    }
};

const ActorPage = () => {
    const { actorId } = useParams();
    const { actor, credits, loading, error } = useActorFetch(actorId);

    if (loading) return <Spinner />;
    if (error || !actor.name) return <div style={{ color: '#fff', padding: '40px', textAlign: 'center' }}>Something went wrong...</div>;

    const profileUrl = actor.profile_path
        ? `${IMAGE_BASE_URL}${BACKDROP_SIZE}${actor.profile_path}`
        : NoImage;

    return (
        <PageWrapper>
            <Helmet>
                <title>{actor.name} — MyCinemaDB</title>
                <meta name="description" content={`Learn about ${actor.name} — biography, filmography, and more on MyCinemaDB.`} />
            </Helmet>

            {/* ─── Hero ─────────────────────────────────────────────── */}
            <Hero>
                <HeroOverlay />
                <ProfileImage
                    src={profileUrl}
                    alt={actor.name}
                    onError={e => { e.target.src = NoImage; }}
                />
                <HeroInfo>
                    <KnownFor>
                        {actor.known_for_department || 'Actor'}
                    </KnownFor>
                    <ActorName>{actor.name}</ActorName>
                    <MetaRow>
                        {actor.birthday && (
                            <MetaItem>
                                <span>Born</span>
                                <span>{formatDate(actor.birthday)}</span>
                            </MetaItem>
                        )}
                        {actor.place_of_birth && (
                            <MetaItem>
                                <span>Birthplace</span>
                                <span>{actor.place_of_birth}</span>
                            </MetaItem>
                        )}
                        {actor.deathday && (
                            <MetaItem>
                                <span>Died</span>
                                <span>{formatDate(actor.deathday)}</span>
                            </MetaItem>
                        )}
                    </MetaRow>
                </HeroInfo>
            </Hero>

            {/* ─── Bio & Filmography ────────────────────────────────── */}
            <Content>
                <BackLink to={-1}>Back</BackLink>

                {actor.biography && (
                    <>
                        <SectionTitle>Biography</SectionTitle>
                        <Bio>{actor.biography}</Bio>
                    </>
                )}

                {credits.length > 0 && (
                    <>
                        <SectionTitle>Known For</SectionTitle>
                        <FilmGrid>
                            {credits.map(film => (
                                <FilmCard key={film.id} to={movieUrl(film.id, film.title)}>
                                    <FilmPoster
                                        src={`${IMAGE_BASE_URL}${POSTER_SIZE}${film.poster_path}`}
                                        alt={film.title}
                                        onError={e => { e.target.src = NoImage; }}
                                    />
                                    <FilmTitle>{film.title}</FilmTitle>
                                </FilmCard>
                            ))}
                        </FilmGrid>
                    </>
                )}
            </Content>
        </PageWrapper>
    );
};

export default ActorPage;
